import { useEffect } from 'react';
import { db, now } from '../db/dexie';
import type { Attachment } from '../db/types';
import { formatBytes } from '../lib/image';
import { basename, useBlobURL } from './AttachmentGallery';

interface Props {
  attachments: Attachment[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
}

export function Lightbox({ attachments, index, onIndex, onClose }: Props) {
  const current = attachments[index];
  const url = useBlobURL(current);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onIndex(Math.max(0, index - 1));
      else if (e.key === 'ArrowRight') onIndex(Math.min(attachments.length - 1, index + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, attachments.length, onIndex, onClose]);

  if (!current) return null;

  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label="Attachment">
      <div className="lightbox__bar">
        <span>
          {basename(current.storage_path)}.webp · {index + 1} of {attachments.length} ·{' '}
          {formatBytes(current.bytes)}
        </span>
        <span className="lightbox__actions">
          <button
            type="button"
            className="lightbox__action"
            onClick={() => {
              if (!url) return;
              const a = document.createElement('a');
              a.href = url;
              a.download = `${basename(current.storage_path)}.webp`;
              a.click();
            }}
          >
            Save a copy
          </button>
          <button
            type="button"
            className="lightbox__action"
            onClick={async () => {
              // Tombstone, not a hard delete — the other device needs to learn.
              await db.attachments.update(current.id, {
                deleted: true,
                updated_at: now(),
                dirty: 1,
              });
              if (attachments.length === 1) onClose();
              else onIndex(Math.max(0, index - 1));
            }}
          >
            Delete
          </button>
          <button type="button" className="kbd" onClick={onClose}>
            esc
          </button>
        </span>
      </div>

      <div className="lightbox__stage">
        <button
          type="button"
          className="lightbox__arrow"
          disabled={index === 0}
          aria-label="Previous"
          onClick={() => onIndex(index - 1)}
        >
          ‹
        </button>
        <div className={`lightbox__frame${url ? '' : ' hatch'}`}>
          {url ? (
            <img src={url} alt={basename(current.storage_path)} />
          ) : (
            <span className="lightbox__placeholder">not downloaded yet</span>
          )}
        </div>
        <button
          type="button"
          className="lightbox__arrow"
          disabled={index === attachments.length - 1}
          aria-label="Next"
          onClick={() => onIndex(index + 1)}
        >
          ›
        </button>
      </div>

      <div className="lightbox__strip">
        {attachments.map((a, i) => (
          <StripItem key={a.id} attachment={a} current={i === index} onClick={() => onIndex(i)} />
        ))}
      </div>
    </div>
  );
}

function StripItem({
  attachment,
  current,
  onClick,
}: {
  attachment: Attachment;
  current: boolean;
  onClick: () => void;
}) {
  const url = useBlobURL(attachment);
  return (
    <button
      type="button"
      className={`lightbox__stripItem${current ? ' is-current' : ''}${url ? '' : ' hatch'}`}
      onClick={onClick}
      aria-label={basename(attachment.storage_path)}
    >
      {url && <img src={url} alt="" />}
    </button>
  );
}
